// /packages/shared-ui/utils/auth.ts
import { Amplify, type ResourcesConfig } from "aws-amplify";
import { type TokenProvider, decodeJWT } from "aws-amplify/auth";
import {
  getRawLocalStorageItem,
  setRawLocalStorageItem,
} from "./localStorage";

// トークンを設定する
type Tokens = {
  tokenStoreName: string;
  accessToken: string;
  idToken?: string;
  refreshToken?: string;
};

// トークンをローカルストレージに保存/取り出しする
const save = (tokens: Tokens) => {
  const { tokenStoreName, accessToken, idToken, refreshToken } = tokens;
  setRawLocalStorageItem(`${tokenStoreName}-accessToken`, accessToken);
  setRawLocalStorageItem(`${tokenStoreName}-idToken`, idToken || "");
  setRawLocalStorageItem(`${tokenStoreName}-refreshToken`, refreshToken || "");
};

const load = async (storeName: string) => {
  const access = getRawLocalStorageItem(`${storeName}-accessToken`);
  const id = getRawLocalStorageItem(`${storeName}-idToken`) || undefined;
  const refresh =
    getRawLocalStorageItem(`${storeName}-refreshToken`) || undefined;

  return { access, id, refresh };
};

// トークンを保存する
export const applyToken = (props: Tokens) => {
  // トークンをSessionStorageに保存
  const { tokenStoreName, accessToken, idToken, refreshToken } = props;
  save({ tokenStoreName, accessToken, idToken, refreshToken });
};

export const setTokenProvider = (
  storeName: string,
  userPoolId: string,
  userPoolClientId: string,
  authRefreshUrl?: string,
) => {
  const tokenProvider = createTokenProvider(storeName, authRefreshUrl);

  //  tokenPrividerをAmplifyに設定
  const resourcesConfig = {
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId,
      },
    },
  } satisfies ResourcesConfig;

  Amplify.configure(resourcesConfig, {
    Auth: { tokenProvider },
  });
};

// Module-level refresh lock to prevent concurrent refresh requests
const refreshLocks = new Map<
  string,
  Promise<{ access: string; id: string; refresh?: string }>
>();

// Amplifyが使用するTokenProvider
const createTokenProvider = (
  storeName: string,
  authRefreshUrl?: string,
): TokenProvider => {
  return {
    async getTokens({ forceRefresh } = {}) {
      const cached = await load(storeName);
      if (!cached || !cached.access) return null; // 未ログイン扱い

      // トークンの有効期限チェック
      const accessToken = decodeJWT(cached.access);
      const now = Math.floor(Date.now() / 1000);
      const expiresAt = accessToken.payload.exp as number;
      const shouldRefresh = expiresAt - now < 300; // 5分前

      // リフレッシュが必要かつリフレッシュURLが提供されている場合
      if ((forceRefresh || shouldRefresh) && authRefreshUrl && cached.refresh) {
        // Check if refresh is already in progress for this store
        const existingRefresh = refreshLocks.get(storeName);
        if (existingRefresh) {
          console.log("Refresh already in progress, waiting...");
          try {
            await existingRefresh;
            // After refresh completes, reload tokens from storage
            const refreshed = await load(storeName);
            if (!refreshed || !refreshed.access) return null;
            return {
              accessToken: decodeJWT(refreshed.access),
              idToken: refreshed.id ? decodeJWT(refreshed.id) : undefined,
              refreshToken: refreshed.refresh,
            };
          } catch (error) {
            console.error("Concurrent refresh failed:", error);
            return null;
          }
        }

        // Start refresh with lock
        const refreshPromise = (async () => {
          try {
            const { callAuthRefresh } = await import("./auth-api");
            const newTokens = await callAuthRefresh(
              cached.refresh!,
              authRefreshUrl,
            );
            save({
              tokenStoreName: storeName,
              accessToken: newTokens.access,
              idToken: newTokens.id,
              refreshToken: newTokens.refresh || cached.refresh, // Preserve old if not provided
            });
            return newTokens;
          } finally {
            refreshLocks.delete(storeName);
          }
        })();

        refreshLocks.set(storeName, refreshPromise);

        try {
          const newTokens = await refreshPromise;
          return {
            accessToken: decodeJWT(newTokens.access),
            idToken: decodeJWT(newTokens.id),
            refreshToken: newTokens.refresh || cached.refresh,
          };
        } catch (error) {
          console.error("Token refresh failed:", error);
          return null; // リフレッシュ失敗 → 再ログイン
        }
      }

      // リフレッシュ不要な場合はキャッシュから返す
      return {
        accessToken: decodeJWT(cached.access),
        idToken: cached.id ? decodeJWT(cached.id) : undefined,
        refreshToken: cached.refresh,
      };
    },
  };
};
